import React from 'react';
import { useTheme } from 'styled-components';
import {
  useMediaQuery,
  Divider,
  Stack,
  MenuItem,
  Theme,
  Box,
  Typography,
} from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { CancelRounded, ShortcutRounded } from '@mui/icons-material';

type MenuContentProps = {
  showCancelButton?: boolean;
  isApprovalPage: boolean | undefined;
  onCancelOrder?: () => void;
  onViewDetails?: any;
  onDecline: () => void;
  onApprove: () => void;
  locale?: any;
};

const ViewDetailsMenuItem = ({
  onViewDetails,
  locale,
}: {
  onViewDetails: () => void;
  locale: Record<string, any>;
}) => {
  const isSmallDown = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm')
  );

  const theme = useTheme();

  return (
    <>
      <MenuItem onClick={onViewDetails} id="btn-menucontent-view_detail">
        <Box display="flex">
          {isSmallDown && (
            <Box display={isSmallDown ? 'flex' : 'block'} paddingRight={4}>
              <ShortcutRounded />
            </Box>
          )}
          <Typography
            sx={{
              color: theme.uptimeColors?.generalColors?.button?.hyperlink?.enabled,
              '&:hover': {
                color: theme.uptimeColors?.generalColors?.button?.hyperlink?.hover,
              },
            }}
          >
            {locale?.viewDetail}
          </Typography>
        </Box>
      </MenuItem>
    </>
  );
};

const ApprovalMenuItems = ({
  onApprove,
  onDecline,
  locale,
}: {
  onApprove: () => void;
  onDecline: () => void;
  locale: any;
}) => {
  return (
    <Box>
      <MenuItem onClick={onApprove} id="btn-menucontent-approve">
        <Box display="flex" alignItems="center">
          <CheckRoundedIcon sx={{ pr: 1, color: 'success.main' }} />
          <Typography>{locale?.approve}</Typography>
        </Box>
      </MenuItem>
      <MenuItem onClick={onDecline} id="btn-menucontent-decline">
        <Box display="flex" alignItems="center">
          <CloseRoundedIcon sx={{ pr: 1, color: 'error.main' }} />
          <Typography>{locale?.decline}</Typography>
        </Box>
      </MenuItem>
    </Box>
  );
};

const OrderMenuItems = ({
  onCancelOrder=() => {},
  locale,
}: {
  onCancelOrder: () => void;
  locale: any;
}) => {
  const isSmallDown = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm')
  );

  return (
    <>
      <MenuItem onClick={onCancelOrder} id="btn-menucontent-cancel">
        <Box display="flex">
          {isSmallDown && (
            <Box display={isSmallDown ? 'flex' : 'block'} paddingRight={4}>
              <CancelRounded color="error" />
            </Box>
          )}
          <Typography
            fontWeight={{ md: 600 }}
            fontSize={{ md: 14 }}
            color="error"
          >
            {locale?.cancelOrder}
          </Typography>
        </Box>
      </MenuItem>
    </>
  );
};

const MenuContent = ({
  showCancelButton = false,
  isApprovalPage,
  onCancelOrder = () => {},
  onViewDetails,
  onDecline,
  onApprove,
  locale,
}: MenuContentProps) => {
  const isMediumDown = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md')
  );

  if (isApprovalPage) {
    return (
      <Stack>
        <ApprovalMenuItems
          onApprove={onApprove}
          onDecline={onDecline}
          locale={locale}
        />
      </Stack>
    );
  }

  return (
    <Stack>
      {isMediumDown && (
        <ViewDetailsMenuItem onViewDetails={onViewDetails} locale={locale} />
      )}
      {showCancelButton && isMediumDown && <Divider />}
      {showCancelButton && (
        <OrderMenuItems onCancelOrder={onCancelOrder} locale={locale} />
      )}
    </Stack>
  );
};

const DrawerContent = ({
  showCancelButton = false,
  isApprovalPage,
  onCancelOrder = () => {},
  onViewDetails,
  onDecline,
  onApprove,
  locale,
}: MenuContentProps) => {
  const isSmallDown = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm')
  );

  if (isApprovalPage) {
    return (
      <Stack flexDirection={isSmallDown ? 'column-reverse' : 'column'}>
        <ApprovalMenuItems
          onApprove={onApprove}
          onDecline={onDecline}
          locale={locale}
        />
      </Stack>
    );
  }

  return (
    <Stack flexDirection={isSmallDown ? 'column-reverse' : 'column'}>
      <ViewDetailsMenuItem onViewDetails={onViewDetails} locale={locale} />
      {showCancelButton && (
        <OrderMenuItems onCancelOrder={onCancelOrder} locale={locale} />
      )}
    </Stack>
  );
};

MenuContent.Drawer = DrawerContent;

export default MenuContent;
